// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { IAction, ActionType, raiseAction } from "actions";
import { DataState, MainPanel } from "types";
import { isSet } from "utils";
import { IState } from "states/states";
import { Site } from "states/sitestates";
import { AppState } from "states/schedulerstates";

import AppStateView from "view/comp/AppState";
import SearchableDropdown from "view/comp/SearchableDropdown";
import MyFormattedTime from "view/comp/FormattedTime";
import { ReactComponent as IconConnect } from "static/imgDisconnect.svg";
import { ReactComponent as IconFavorite } from "static/iconFavorite.svg";
import { tabClickHandler } from "guiutils";

const CONNECTION_TIMEOUT = 10; // s, after which the offline link will be shown

interface ISiteExProps {}

interface ISiteProps {
  appState: AppState;
  siteList: Site[] | undefined; // undefined if not loaded yet
  selectedSiteId: number | undefined;
  parameterDataState: DataState;
  hasParameter: boolean;
  lastLiveData: number | undefined;
  lastTrendData: number | undefined;
  isFavorite: boolean;
  favoriteSites: number[] | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: ISiteProps = {
    appState: state.scheduler.appState,
    siteList: state.site.siteList ?? [],
    selectedSiteId: state.cached.selectedSite,
    parameterDataState: state.scheduler.parameterDataState,
    hasParameter: isSet(state.scheduler.parameter.lastData),
    lastLiveData: state.scheduler.allData.lastData,
    lastTrendData: state.scheduler.trendLoading.lastData,
    isFavorite: state.cached.favoriteSites?.includes(state.cached.selectedSite!) ?? false,
    favoriteSites: state.cached.favoriteSites,
  };
  return props;
};

interface ISiteState {
  selectedSiteIndex: number | undefined;
  lastState: AppState;
  lastStateSwitch: number | undefined;
}

interface ISiteDispatch {
  onSelectSite: (id: number) => void;
  onDisconnectClick: () => void;
  onGoOfflineClick: () => void;
  onSetFavorite: (isFavorite: boolean) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteDispatch = {
    onSelectSite: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_SELECT_SITE, id));
    },
    onDisconnectClick: () => {
      dispatch(raiseAction(ActionType.ACT_DISCONNECT_SITE));
    },
    onGoOfflineClick: () => {
      dispatch(raiseAction(ActionType.ACT_INIT_DONE));
    },
    onSetFavorite: (isFavorite) => {
      dispatch(raiseAction(ActionType.ACT_SET_FAVORITE, isFavorite));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_select_site: {
    id: "Select site",
  },
  msg_favorite: {
    id: "Favorite",
  },
});

class SiteComponent extends React.Component<
  ISiteProps & ISiteExProps & ISiteDispatch & WrappedComponentProps & RoutedProps,
  ISiteState
> {
  state: ISiteState = {
    selectedSiteIndex: undefined,
    lastState: AppState.INIT,
    lastStateSwitch: undefined,
  };

  public componentDidUpdate() {
    if (this.props.appState !== this.state.lastState) {
      this.setState({ lastState: this.props.appState, lastStateSwitch: Date.now() });
    }
    if (this.props.appState === AppState.LIVE_DATA_SUBSCRIBED) {
      setTimeout(() => {
        // ensure state update to recheck after 1 s
        this.setState({});
      }, 1000);
    }
  }

  public render() {
    const selectedSite = this.props.siteList?.find(
      (curSite) => curSite.id === this.props.selectedSiteId,
    );
    const view = (
      <div id="site">
        <div id="site_inner">
          <IconConnect id="site_left" width={50} />
          <div id="site_right">
            {this.props.siteList === undefined
              ? this.renderLoadingSitelist()
              : selectedSite === undefined
              ? this.props.siteList.length
                ? this.renderSiteConnection()
                : this.renderNoSite()
              : this.renderConnected()}
          </div>
        </div>
      </div>
    );

    return view;
  }

  private renderSiteConnection() {
    const siteIds = Array.from(Array(this.props.siteList!.length).keys()).sort((a, b) =>
      this.props.siteList![a].name.localeCompare(this.props.siteList![b].name),
    );

    return (
      <>
        <h1>
          <FormattedMessage id="Connect to Site" />
        </h1>
        <p>
          <FormattedMessage id="Currently, you are not connected to a site." />{" "}
          <FormattedMessage id="You need to connect to the site to see live data or to make any changes." />
        </p>
        <p>
          <FormattedMessage id="Please select the site you want to monitor." />
        </p>

        <div className="buttonbar">
          <SearchableDropdown
            caption={this.props.intl.formatMessage(messages.msg_select_site)}
            valueList={siteIds}
            mapperFunction={(value: number | undefined) => {
              this.setState({ selectedSiteIndex: value });
            }}
            labelFunction={(index: number) => this.props.siteList![index].name}
            curItem={
              isSet(this.state.selectedSiteIndex)
                ? this.props.siteList?.[this.state.selectedSiteIndex]?.name
                : this.props.intl.formatMessage(messages.msg_select_site)
            }
            nextElementId="connect"
          />
          <Button
            id="connect"
            className="ms-2"
            type="submit"
            variant="primary"
            onClick={this.props.onSelectSite.bind(
              this,
              this.props.siteList?.[this.state.selectedSiteIndex!]?.id ?? 0,
            )}
            disabled={this.state.selectedSiteIndex === undefined}
          >
            <FormattedMessage id="Connect" />
          </Button>
        </div>
        {this.renderFavorites()}
      </>
    );
  }

  private renderFavorites() {
    // extract favorites from available sites
    const favoriteSites = this.props.siteList?.filter((curSite) =>
      this.props.favoriteSites?.includes(curSite.id),
    );
    if (!favoriteSites || favoriteSites.length === 0) {
      return <></>;
    }
    const sites = favoriteSites.map((curSite) => (
      <li
        key={curSite.id}
        tabIndex={0}
        onKeyDown={tabClickHandler}
        className="text-link mt-1"
        onClick={() => this.props.onSelectSite(curSite.id)}
      >
        {curSite.name}
      </li>
    ));
    return (
      <>
        <h2 style={{ marginTop: "2em" }}>
          <FormattedMessage id="Favorites" />{" "}
          <span className="favorite active">
            <IconFavorite style={{ marginTop: -4 }} />
          </span>
        </h2>
        <ul>{sites}</ul>
      </>
    );
  }

  private renderLoadingSitelist() {
    return (
      <>
        <h1>
          <FormattedMessage id="Connect to Site" />
        </h1>
        <p>
          <FormattedMessage id="Loading the list of available sites. Please wait." />
        </p>
      </>
    );
  }

  private renderNoSite() {
    return (
      <>
        <h1>
          <FormattedMessage id="Connect to Site" />
        </h1>
        <p>
          <FormattedMessage id="Currently, there is no site available, which you can connect to." />{" "}
        </p>
        <p>
          <FormattedMessage id="Please contact the system administrator to assign a site to your user." />
        </p>
      </>
    );
  }

  private renderConnected() {
    const site = this.props.siteList?.find((curItem) => curItem.id === this.props.selectedSiteId);
    const isConnectTimedout =
      this.props.appState === AppState.LIVE_DATA_SUBSCRIBED &&
      isSet(this.state.lastStateSwitch) &&
      (Date.now() - this.state.lastStateSwitch) / 1000 > CONNECTION_TIMEOUT;

    const ipAddresses = site?.ipAddresses ? `IP Addresses: ${site.ipAddresses}` : undefined;

    return (
      <>
        <h1>
          <FormattedMessage id="Current Site" />
        </h1>
        <p>
          <FormattedMessage id="You are connected to this site:" />
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                <FormattedMessage id="Name" />:
              </td>
              <td>
                <span title={`Id: ${site?.id}`} className="me-1">
                  {site?.name}
                </span>
                <span
                  title={this.props.intl.formatMessage(messages.msg_favorite)}
                  className={`favorite link ${this.props.isFavorite ? "active" : ""}`}
                  tabIndex={0}
                  onKeyDown={tabClickHandler}
                  onClick={() => this.props.onSetFavorite(!this.props.isFavorite)}
                >
                  <IconFavorite style={{ marginTop: -4 }} />
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Device" />:
              </td>
              <td>
                {site?.deviceType && <FormattedMessage id={`DeviceType_${site.deviceType}`} />}
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Serial" />:
              </td>
              <td title={ipAddresses}>{site?.serial}</td>
            </tr>
            {isSet(site?.deviceSw) && site?.deviceSw !== "" && (
              <tr>
                <td>
                  <FormattedMessage id="Software" />:
                </td>
                <td>{site.deviceSw.replace(/-g[0-9a-f]{8}$/, "")}</td>
              </tr>
            )}
            <tr>
              <td>
                <FormattedMessage id="Trend / Live Data" />:
              </td>
              <td>
                <MyFormattedTime
                  valueMs={this.props.lastTrendData}
                  showSeconds={true}
                  nullText={"-"}
                />{" "}
                /{" "}
                <MyFormattedTime
                  valueMs={this.props.lastLiveData}
                  showSeconds={true}
                  nullText={"-"}
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div className="buttonbox">
          <Button
            variant="secondary"
            id="logout"
            type="submit"
            onClick={this.props.onDisconnectClick}
          >
            <FormattedMessage id="Disconnect" />
          </Button>
          {isConnectTimedout && (
            <Button
              id="gooffline"
              type="submit"
              variant="danger"
              onClick={this.props.onGoOfflineClick}
            >
              <FormattedMessage id="Connect Offline" />
            </Button>
          )}
          {this.props.appState >= AppState.READY && (
            <Button
              variant="secondary"
              id="modifySiteSettings"
              onClick={() => this.props.navigate(`../${MainPanel.SITE_SETTINGS}`)}
            >
              <FormattedMessage id="Site Settings" />
            </Button>
          )}
        </div>
        <AppStateView />
      </>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withRouter(SiteComponent)));
